// src/pages/GearAdd.jsx

import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useGearStore } from "../store/gearStore";
import { useTeamStore } from "../store/teamStore";
import { useProjectStore } from "../store/projectStore";
import GearForm from "../components/GearForm";

export default function GearAdd({ setCurrentPage }) {
  const navigate = useNavigate();
  const { setGear, categories, statuses, fields } = useGearStore();
  const { team } = useTeamStore();
  const { projects, fetchProjects } = useProjectStore();

  const teamList = Array.isArray(team)
    ? team
    : Array.isArray(team?.members)
    ? team.members
    : Array.isArray(team?.items)
    ? team.items
    : [];

  const projectsList = useMemo(
    () => (Array.isArray(projects) ? projects : []),
    [projects]
  );

  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (projectsList.length === 0) {
      fetchProjects && fetchProjects();
    }
  }, [projectsList, fetchProjects]);

  const unwrap = (res) => {
    const payload = res && typeof res === "object" && "data" in res ? res.data : res;
    return payload && typeof payload === "object" ? payload : null;
  };

  const handleSave = async (data) => {
    setSaveError("");

    try {
      const res = await api.post("/gear", data);
      const created = unwrap(res);

      if (!created?.id) {
        throw new Error("Backend did not return created gear with an id.");
      }

      setGear((prev) => [created, ...(Array.isArray(prev) ? prev : [])]);

      // Go back to Gear list
      setCurrentPage && setCurrentPage("gear");
      navigate("/gear");
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to save gear.";
      setSaveError(msg);
      throw new Error(msg);
    }
  };

  return (
    <div className="w-full min-h-[90vh] flex flex-col items-center px-0 sm:px-4 py-3 sm:py-8 bg-[#f8fafd] pb-28 sm:pb-0">
      <div className="w-full max-w-3xl bg-white rounded-none sm:rounded-xl shadow-none sm:shadow-xl p-3 sm:p-8 md:px-10 relative">
        <div className="flex items-center justify-between gap-3 mb-4">
          <button
            className="text-blue-600 flex items-center hover:underline w-fit"
            onClick={() => {
              setCurrentPage && setCurrentPage("gear");
              navigate("/gear");
            }}
            type="button"
          >
            <ArrowLeft size={20} className="mr-1" />
            Back
          </button>

          <div className="text-lg font-bold text-gray-800">Add Equipment</div>
          <div className="w-10" />
        </div>

        {saveError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            {saveError}
          </div>
        )}

        <GearForm
          gear={null}
          onClose={() => {
            setCurrentPage && setCurrentPage("gear");
            navigate("/gear");
          }}
          onSave={handleSave}
          team={teamList}
          projects={projectsList}
          categories={Array.isArray(categories) ? categories : []}
          statuses={Array.isArray(statuses) ? statuses : []}
          fields={Array.isArray(fields) ? fields : []}
          isAdminOrManager={true}
          asPage={true}
          showTitle={false}
        />
      </div>
    </div>
  );
}
